import React, { useState, useEffect } from 'react';
import api, { documentAPI } from '../services/api';
import SearchableSelect from './SearchableSelect';
import Swal from 'sweetalert2';
import { generateLicensePlateReport } from '../utils/generateLicensePlateReport';
import { useAuth } from '../context/AuthContext';

const BatchPlateInput = () => {
  const { user } = useAuth();
  const [provinces, setProvinces] = useState([]);
  const [formData, setFormData] = useState({
    provinceId: '',
    series: '',
    letter: '',
    startNumber: '',
    endNumber: '',
    color: 'trắng',
    unitType: 'bộ',
    vehicleType: 'ô tô'
  });
  const [previewPlates, setPreviewPlates] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPlates, setSelectedPlates] = useState([]);

  useEffect(() => {
    fetchProvinces();
  }, []);

  const fetchProvinces = async () => {
    try {
      const response = await api.get('/provinces');
      setProvinces(response.data);
      
      const lamDong = response.data.find(p => p.code === '49');
      if (lamDong) {
        setFormData(prev => ({
          ...prev,
          provinceId: lamDong.id,
          series: '49'
        }));
      }
    } catch (error) {
      console.error('Error fetching provinces:', error);
    }
  };

  const handleProvinceChange = (provinceId) => {
    const province = provinces.find(p => p.id === parseInt(provinceId));
    setFormData(prev => ({
      ...prev,
      provinceId,
      series: province ? province.code : ''
    }));
  };

  const handleLetterChange = (value) => {
    const upperValue = value.toUpperCase();
    // Cho phép chữ cái (A-Z) và số (0-9), tối đa 2 ký tự
    if (upperValue.length <= 2 && /^[A-Z0-9]*$/.test(upperValue)) {
      setFormData(prev => ({ ...prev, letter: upperValue }));
    }
  };

  const handleNumberChange = (field, value) => {
    if (value.length <= 5 && /^\d*$/.test(value)) {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleGeneratePreview = async () => {
    if (!formData.provinceId || !formData.letter || !formData.startNumber || !formData.endNumber) {
      Swal.fire({
        icon: 'warning',
        title: 'Thiếu thông tin',
        text: 'Vui lòng nhập đầy đủ thông tin',
        confirmButtonColor: '#1976d2'
      });
      return;
    }

    if (formData.startNumber.length !== 5 || formData.endNumber.length !== 5) {
      Swal.fire({
        icon: 'warning',
        title: 'Số không hợp lệ',
        text: 'Số bắt đầu và số kết thúc phải có 5 chữ số',
        confirmButtonColor: '#1976d2'
      });
      return;
    }

    try {
      setLoading(true);
      
      const payload = {
        provinceId: formData.provinceId,
        series: `${formData.series}${formData.letter}`,
        startNumber: formData.startNumber,
        endNumber: formData.endNumber,
        color: formData.color,
        unitType: formData.unitType,
        vehicleType: formData.vehicleType
      };

      const response = await api.post('/license-plates/batch/preview', payload);
      setPreviewPlates(response.data.allPlates); // Dùng allPlates thay vì plates
      setConflicts(response.data.conflicts);
      
      // Tự động chọn tất cả biển số không bị trùng
      const nonConflictIndexes = response.data.allPlates
        .map((plate, index) => plate.isConflict ? null : index)
        .filter(index => index !== null);
      setSelectedPlates(nonConflictIndexes);
      
      Swal.fire({
        icon: 'info',
        title: 'Đã tạo dãy biển số',
        html: `
          <div style="text-align: left;">
            <p>✅ Có thể tạo: <strong>${response.data.available}</strong> biển số</p>
            ${response.data.conflicted > 0 ? `<p>❌ Bị trùng: <strong>${response.data.conflicted}</strong> biển số</p>` : ''}
          </div>
        `,
        confirmButtonColor: '#1976d2'
      });
    } catch (error) {
      console.error('Error generating preview:', error);
      Swal.fire({
        icon: 'error',
        title: 'Lỗi!',
        text: error.response?.data?.message || 'Lỗi khi tạo dãy biển số',
        confirmButtonColor: '#1976d2'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSelect = (index) => {
    setSelectedPlates(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedPlates.length === previewPlates.length) {
      setSelectedPlates([]);
    } else {
      setSelectedPlates(previewPlates.map((_, index) => index));
    }
  };

  const handleConfirmCreate = async () => {
    if (selectedPlates.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Chưa chọn biển số',
        text: 'Vui lòng chọn ít nhất 1 biển số',
        confirmButtonColor: '#1976d2'
      });
      return;
    }

    try {
      setLoading(true);
      
      // Chỉ lấy các biển số được chọn và không bị trùng
      const platesToCreate = selectedPlates
        .map(index => previewPlates[index])
        .filter(plate => !plate.isConflict)
        .map(plate => {
          // Loại bỏ các field không cần thiết
          const { isConflict, existingUnit, ...plateData } = plate;
          return plateData;
        });
      
      if (platesToCreate.length === 0) {
        Swal.fire({
          icon: 'warning',
          title: 'Không có biển số hợp lệ',
          text: 'Tất cả biển số đã chọn đều bị trùng',
          confirmButtonColor: '#1976d2'
        });
        return;
      }
      
      await api.post('/license-plates/batch/confirm', { plates: platesToCreate });
      
      // Hỏi có muốn tải báo cáo
      const result = await Swal.fire({
        icon: 'success',
        title: 'Thành công!',
        text: `Đã tạo thành công ${platesToCreate.length} biển số`,
        showCancelButton: true,
        confirmButtonText: '📄 Tải báo cáo',
        cancelButtonText: 'Đóng',
        confirmButtonColor: '#4caf50',
        cancelButtonColor: '#6b7280'
      });

      if (result.isConfirmed && user && user.unit) {
        // Generate báo cáo
        try {
          const reportData = await generateLicensePlateReport({
            unitName: user.unit.name,

            provinceCode: formData.series.replace(/[A-Z]/g, ''), // Lấy số tỉnh từ series
            series: formData.letter,
            plates: platesToCreate,
            color: formData.color,
            unitType: formData.unitType,
            isBatch: true // Biển số dãy
          });

          // 1. Tải file về máy
          const { saveAs } = await import('file-saver');
          saveAs(reportData.blob, reportData.fileName);

          // 2. Upload lên server
          const uploadFormData = new FormData();
          uploadFormData.append('document', reportData.blob, reportData.fileName);
          uploadFormData.append('title', reportData.fileName.replace('.docx', ''));

          await documentAPI.uploadDocument(uploadFormData);

          Swal.fire({
            icon: 'success',
            title: 'Đã tải báo cáo!',
            text: 'Báo cáo đã được tải về máy và lưu vào Quản lý tài liệu',
            confirmButtonColor: '#4caf50'
          });
        } catch (error) {
          console.error('Error generating/uploading report:', error);
          Swal.fire({
            icon: 'error',
            title: 'Lỗi!',
            text: 'Không thể tạo hoặc upload báo cáo',
            confirmButtonColor: '#1976d2'
          });
        }
      }
      
      // Reset form
      handleResetPreview();
    } catch (error) {
      console.error('Error confirming plates:', error);
      Swal.fire({
        icon: 'error',
        title: 'Lỗi!',
        text: error.response?.data?.message || 'Lỗi khi tạo biển số',
        confirmButtonColor: '#1976d2'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPreview = () => {
    setPreviewPlates([]);
    setConflicts([]);
    setSelectedPlates([]);
    setFormData(prev => ({
      ...prev,
      letter: '',
      startNumber: '',
      endNumber: ''
    }));
  };

  const isConflict = (plate) => {
    return plate.isConflict === true;
  };

  const getConflictUnit = (plate) => {
    return plate.existingUnit;
  };

  // Render biển số theo dạng 6 cột
  const renderPlatesGrid = () => {
    const rows = [];
    for (let i = 0; i < previewPlates.length; i += 6) {
      rows.push(previewPlates.slice(i, i + 6));
    }

    return rows.map((row, rowIndex) => (
      <div key={rowIndex} className="plates-row">
        {row.map((plate, colIndex) => {
          const index = rowIndex * 6 + colIndex;
          const conflict = isConflict(plate);
          const conflictUnit = getConflictUnit(plate);
          
          return (
            <div key={index} className="plate-cell">
              <div className={`plate-input-wrapper ${conflict ? 'conflict' : ''}`}>
                <input
                  type="checkbox"
                  checked={selectedPlates.includes(index)}
                  onChange={() => handleToggleSelect(index)}
                  disabled={conflict}
                  className="plate-checkbox"
                />
                <input
                  type="text"
                  value={plate.fullPlate}
                  readOnly
                  className={`plate-input ${conflict ? 'error' : ''}`}
                />
                {conflict && (
                  <span className="conflict-unit">{conflictUnit}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    ));
  };

  // Chuyển đổi provinces thành options cho SearchableSelect
  const provinceOptions = provinces.map(p => ({
    value: p.id,
    label: `${p.code} - ${p.name}`
  }));

  return (
    <div className="batch-plate-input">
      <div className="batch-form-container">
        <div className="batch-row-header">
          <div className="header-cell">Tỉnh/TP</div>
          <div className="header-cell">Loại xe</div>
          <div className="header-cell">Seri</div>
          <div className="header-cell">Số bắt đầu</div>
          <div className="header-cell">Số kết thúc</div>
          <div className="header-cell">Màu sắc</div>
          <div className="header-cell">Đơn vị</div>
        </div>

        <div className="batch-row">
          <div className="row-cell">
            <SearchableSelect
              options={provinceOptions}
              value={formData.provinceId}
              onChange={handleProvinceChange}
              placeholder="Chọn tỉnh/TP"
            />
          </div>

          <div className="row-cell">
            <select
              value={formData.vehicleType}
              onChange={(e) => {
                const newVehicleType = e.target.value;
                setFormData(prev => ({
                  ...prev,
                  vehicleType: newVehicleType,
                  // Tự động set unitType dựa vào vehicleType
                  unitType: newVehicleType === 'mô tô' ? 'biển' : 'bộ'
                }));
              }}
              required
            >
              <option value="ô tô">Ô tô</option>
              <option value="mô tô">Mô tô</option>
              <option value="rơ moóc">Rơ moóc</option>
            </select>
          </div>

          <div className="row-cell series-cell">
            <input
              type="text"
              value={formData.series}
              disabled
              className="series-code-inline"
            />
            <input
              type="text"
              value={formData.letter}
              onChange={(e) => handleLetterChange(e.target.value)}
              placeholder="AB/A1"
              maxLength="2"
              className="series-letter-inline"
              required
            />
          </div>

          <div className="row-cell">
            <input
              type="text"
              value={formData.startNumber}
              onChange={(e) => handleNumberChange('startNumber', e.target.value)}
              placeholder="00001"
              maxLength="5"
              required
            />
          </div>

          <div className="row-cell">
            <input
              type="text"
              value={formData.endNumber}
              onChange={(e) => handleNumberChange('endNumber', e.target.value)}
              placeholder="00099"
              maxLength="5"
              required
            />
          </div>

          <div className="row-cell">
            <select
              value={formData.color}
              onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
              required
            >
              <option value="trắng">Trắng</option>
              <option value="vàng">Vàng</option>
              <option value="xanh">Xanh</option>
            </select>
          </div>

          <div className="row-cell">
            <select
              value={formData.unitType}
              onChange={(e) => setFormData(prev => ({ ...prev, unitType: e.target.value }))}
              required
            >
              {formData.vehicleType === 'mô tô' ? (
                // Mô tô: chỉ có "Biển"
                <option value="biển">Biển</option>
              ) : (
                // Ô tô/Rơ moóc: có các options khác (không có "biển")
                <>
                  <option value="bộ">Bộ</option>
                  <option value="bộ dài">Bộ dài</option>
                  <option value="bộ ngắn">Bộ ngắn</option>
                  <option value="biển dài">Biển dài</option>
                  <option value="biển ngắn">Biển ngắn</option>
                </>
              )}
            </select>
          </div>
        </div>

        <div className="form-actions-bottom">
          <button
            type="button"
            onClick={handleGeneratePreview}
            disabled={loading}
            className="btn-preview"
          >
            {loading ? 'Đang xử lý...' : 'Tạo dãy biển số'}
          </button>
        </div>
      </div>

      {previewPlates.length > 0 && (
        <div className="preview-section">
          <div className="preview-header">
            <h3>Danh sách biển số ({previewPlates.length} biển)</h3>
            <div className="preview-actions">
              <button onClick={handleResetPreview} className="btn-reset-preview">
                🔄 Làm mới
              </button>
              <button onClick={handleSelectAll} className="btn-select-all">
                {selectedPlates.length === previewPlates.length ? 'Bỏ chọn tất cả' : 'Chọn tất cả'}
              </button>
              <button
                onClick={handleConfirmCreate}
                disabled={loading || selectedPlates.length === 0}
                className="btn-confirm"
              >
                Tạo {selectedPlates.length} biển số đã chọn
              </button>
            </div>
          </div>

          <div className="plates-grid">
            {renderPlatesGrid()}
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchPlateInput;
